import { Component, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiClientService } from '../core/api-client.service';
import { PageDataService } from '../core/page-data.service';
import { SeoService } from '../core/seo.service';
import { SiteConfigService } from '../core/site-config.service';
import { I18nService } from '../core/i18n.service';
import { AuthService } from '../core/auth.service';
import { LocalePreferenceService } from '../core/locale-preference.service';

interface WalletTopUpPackage {
  id: number;
  creditPackageID: number;
  name: string;
  description: string;
  amount: number;
  currency: string;
  productID: string;
  walletTopUpPresetID: number;
  highlighted: boolean;
  popular: boolean;
  creditAmount?: number;
}
interface WalletCampaign {
  id: number;
  title: string;
  userRole: string | null;
  campaignType: string;
  discountType: string | null;
  discountValue: number | null;
  minAmount: number | null;
  maxAmount: number | null;
  listingIDs: number[];
  usageLimit: number | null;
  userUsageLimit: number | null;
  endDate: string | null;
}
interface CreditCopy {
  eyebrow: string; title: string; lead: string; campaignsTitle: string; credit: string;
  popular: string; checkoutEyebrow: string; choose: string; checkoutLead: string;
  total: string; loading: string; pay: string; firstPurchase: string; threshold: string;
  bonus: string; from: string; balance?: string; credited?: string; empty?: string;
  apply: string; applied: string;
  package: string; packageDescription: string; verifying: string; startFailed: string; loadFailed: string;
  lastUse: string; creditedResuming: string; creditedReady: string; paymentFailed: string;
  bankPending: string; verificationPending: string; notificationPending: string;
}

@Component({
  selector: 'app-credit-top-up-page',
  imports: [],
  template: `
    <section class="credit-layout">
      <h1 class="sr-only">{{ copy().eyebrow }}</h1>
      @if (notice()) {
        <div class="payment-notice" [class.success]="noticeSuccess()">{{ notice() }}</div>
      }
      @if (balance() !== null) {
        <div class="credit-balance" [class.completed]="noticeSuccess()" [class.celebrate]="balanceAnimating()">
          <div>
          <span>{{ balanceLabel() }}</span>
          <strong>{{ numberText(balance()) }} <small>{{ copy().credit }}</small></strong>
          </div>
          @if (noticeSuccess()) {
            <p>{{ creditedLabel() }}</p>
            <i class="spark spark-one"></i>
            <i class="spark spark-two"></i>
            <i class="spark spark-three"></i>
          }
        </div>
      }
      <div class="packages">
        @for (item of packages(); track item.creditPackageID || item.id) {
          <button type="button" class="package-card" [class.selected]="selected()?.id === item.id" [class.popular-card]="item.popular" [attr.aria-pressed]="selected()?.id === item.id" (click)="selectPackage(item)">
            @if (item.popular) {
              <span class="badge"><span aria-hidden="true">✦</span> {{ copy().popular }}</span>
            }
            <strong>{{ packageName(item) }}</strong>
            <span class="amount">{{ numberText(item.amount) }} {{ currencyLabel(item.currency) }}</span>
            @if (campaignBonus(item) > 0) { <span class="gift-line"><del>{{ numberText(campaignBase(item)) }}</del> {{ numberText(campaignBase(item) + campaignBonus(item)) }} {{ copy().credit }}</span> }
            <small>{{ packageDescription(item) }}</small>
            @if (selected()?.id === item.id) {
              <span class="selected-mark">{{ selectedLabel() }} <span aria-hidden="true">✓</span></span>
            }
          </button>
        }
        @if (!packages().length) {
          <div class="empty">{{ emptyLabel() }}</div>
        }
      </div>

      <aside class="checkout-card" [class.has-selection]="!!selected()">
        <p class="eyebrow">{{ copy().checkoutEyebrow }}</p>
        <h2>{{ selected() ? packageName(selected()!) : copy().choose }}</h2>
        <p>{{ selected() ? packageDescription(selected()!) : copy().checkoutLead }}</p>
        <div class="summary">
          <span>{{ copy().total }}</span>
          <strong>{{ numberText(selected()?.amount || 0) }} {{ currencyLabel(selected()?.currency || 'TRY') }} @if (selected() && campaignBonus(selected()!) > 0) { · {{ numberText(campaignBase(selected()!) + campaignBonus(selected()!)) }} {{ copy().credit }} }</strong>
        </div>
        @if (campaigns().length) {
          <section class="checkout-campaigns" [attr.aria-label]="copy().campaignsTitle">
            <span class="campaign-heading">{{ copy().threshold }}</span>
            @for (campaign of campaigns(); track campaign.id) {
              <article class="campaign-option" [class.campaign-option--applied]="appliedCampaign()?.id === campaign.id">
                <div>
                  <strong>{{ campaignLabel(campaign) }}</strong>
                  <small>{{ campaignDescription(campaign) }} @if (campaignEnd(campaign)) { · {{ campaignEnd(campaign) }} }</small>
                </div>
                <button type="button" class="campaign-apply" [disabled]="!campaignCanApply(campaign, selected())" (click)="applyCampaign(campaign)">{{ appliedCampaign()?.id === campaign.id ? copy().applied : copy().apply }}</button>
              </article>
            }
          </section>
        }
        @if (error()) {
          <div class="error">{{ error() }}</div>
        }
        <button type="button" class="purchase-button" [disabled]="!selected() || loading()" (click)="startTopUp()">
          {{ loading() ? copy().loading : (selected() ? selectedPurchaseLabel(selected()!) : copy().pay) }}
        </button>
        @if (payload(); as data) {
          <pre>{{ data }}</pre>
        }
      </aside>
    </section>
  `,
  styles: [`
    :host-context(.theme-core-page) { display:block; margin-top:0; }

    .credit-layout {
      margin: 0 auto;
      max-width: 1180px;
    }

    .credit-layout {
      display: grid;
      gap: 20px;
      grid-template-columns: minmax(0, 1fr) 360px;
      padding: 30px 20px 80px;
    }

    .sr-only { height:1px; margin:-1px; overflow:hidden; padding:0; position:absolute; width:1px; clip:rect(0,0,0,0); white-space:nowrap; }

    .packages {
      display: grid;
      gap: 10px;
      grid-template-columns: 1fr;
    }

    .checkout-campaigns { border-top:1px solid var(--border); display:grid; gap:8px; padding-top:14px; }
    .campaign-heading { color:var(--accent); font-size:.72rem; font-weight:950; letter-spacing:.12em; text-transform:uppercase; }
    .campaign-option { align-items:center; background:color-mix(in srgb,var(--accent) 7%,var(--surface)); border:1px solid var(--border); border-radius:16px; display:grid; gap:10px; grid-template-columns:minmax(0,1fr) auto; padding:10px 11px; }
    .campaign-option--applied { background:color-mix(in srgb,var(--accent) 13%,var(--surface)); border-color:var(--accent); }
    .campaign-option > div { display:grid; gap:2px; min-width:0; }
    .campaign-option strong { color:var(--ink); font-size:.92rem; }
    .campaign-option small { color:var(--muted); font-size:.72rem; line-height:1.35; }
    .checkout-card .campaign-apply { background:var(--ink); border:0; border-radius:999px; color:var(--paper); cursor:pointer; font:inherit; font-size:.78rem; font-weight:900; padding:8px 11px; white-space:nowrap; }
    .checkout-card .campaign-apply:disabled { cursor:not-allowed; opacity:.4; }
    .gift-line { color:var(--accent); font-size:.9rem; font-weight:900; }
    .gift-line del { color:var(--muted); font-weight:600; margin-right:5px; }

    .payment-notice {
      background: color-mix(in srgb, #d97706 14%, var(--surface));
      border: 1px solid color-mix(in srgb, #d97706 36%, var(--border));
      border-radius: 22px;
      grid-column: 1 / -1;
      padding: 16px 20px;
    }

    .payment-notice.success {
      background: color-mix(in srgb, #16834a 14%, var(--surface));
      border-color: color-mix(in srgb, #16834a 36%, var(--border));
    }

    .credit-balance {
      align-items: center;
      background: radial-gradient(circle at 84% 20%, color-mix(in srgb, var(--accent) 20%, transparent), transparent 38%), var(--card);
      border: 1px solid var(--border);
      border-radius: 22px;
      display: flex;
      grid-column: 1 / -1;
      justify-content: space-between;
      overflow: hidden;
      padding: 12px 18px;
      position: relative;
    }

    .credit-balance > div {
      align-items: baseline;
      display: flex;
      gap: 10px;
    }

    .credit-balance span,
    .credit-balance p {
      color: var(--muted);
      margin: 0;
    }

    .credit-balance strong {
      color: var(--ink);
      font-size: clamp(22px, 3vw, 30px);
      letter-spacing: -.04em;
    }

    .credit-balance strong small {
      color: var(--accent);
      font-size: .42em;
      letter-spacing: 0;
    }

    .credit-balance.completed {
      border-color: color-mix(in srgb, #35c779 52%, var(--border));
    }

    .credit-balance.completed p {
      color: #35c779;
      font-weight: 900;
    }

    .credit-balance.celebrate strong {
      animation: credit-pop 850ms cubic-bezier(.2, .9, .2, 1);
    }

    .spark {
      background: var(--accent);
      border-radius: 50%;
      height: 8px;
      opacity: 0;
      pointer-events: none;
      position: absolute;
      right: 22%;
      top: 50%;
      width: 8px;
    }

    .celebrate .spark {
      animation: credit-spark 900ms ease-out forwards;
    }

    .celebrate .spark-two { animation-delay: 80ms; right: 12%; }
    .celebrate .spark-three { animation-delay: 150ms; right: 29%; }

    @keyframes credit-pop {
      0% { opacity: .35; transform: scale(.82); }
      55% { transform: scale(1.08); }
      100% { opacity: 1; transform: scale(1); }
    }

    @keyframes credit-spark {
      0% { opacity: 0; transform: translate3d(0, 0, 0) scale(.3); }
      35% { opacity: 1; }
      100% { opacity: 0; transform: translate3d(28px, -52px, 0) scale(1.4); }
    }

    .empty {
      background: var(--card);
      border: 1px solid var(--border);
      border-radius: 24px;
      color: var(--muted);
      grid-column: 1 / -1;
      padding: 24px;
    }

    .package-card,
    .checkout-card {
      background: var(--card);
      border: 1px solid var(--border);
      border-radius: 4px;
      box-shadow: 0 2px 6px color-mix(in srgb, var(--shadow) 34%, transparent);
    }

    .package-card {
      align-items: center;
      color: var(--ink);
      cursor: pointer;
      display: grid;
      gap: 5px 16px;
      grid-template-columns: minmax(170px, 1fr) auto auto;
      min-height: 86px;
      padding: 14px 16px;
      position: relative;
      text-align: left;
      transition: background .15s ease, border-color .15s ease, box-shadow .15s ease;
    }

    .package-card:hover {
      border-color: color-mix(in srgb, var(--accent) 62%, var(--border));
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent) 12%, transparent);
    }

    .package-card.selected {
      background: #f1e6fa;
      border: 3px solid var(--accent);
      box-shadow: inset 5px 0 0 var(--accent), 0 0 0 2px #d9c0ed;
      padding: 12px 14px;
    }

    .package-card.popular-card {
      border-color: color-mix(in srgb, var(--accent) 58%, var(--border));
    }

    .package-card.popular-card::after {
      content: none;
    }

    .badge,
    .amount {
      color: var(--accent);
      font-weight: 950;
    }

    .badge {
      align-items: center;
      background: color-mix(in srgb, var(--accent) 12%, var(--surface));
      border: 1px solid color-mix(in srgb, var(--accent) 40%, var(--border));
      border-radius: 999px;
      display: inline-flex;
      gap: 5px;
      grid-column: 3;
      grid-row: 1 / span 2;
      justify-self: end;
      letter-spacing: .04em;
      padding: 6px 10px;
      text-transform: uppercase;
    }

    .badge span {
      font-size: 1.15em;
    }

    .package-card strong {
      font-size: 21px;
      grid-column: 1;
      letter-spacing: -.03em;
    }

    .package-card .amount {
      font-size: 1.08rem;
      grid-column: 2;
      grid-row: 1 / span 2;
      white-space: nowrap;
    }

    .package-card small {
      background: color-mix(in srgb, var(--paper) 58%, transparent);
      border-radius: 12px;
      color: var(--muted);
      justify-self: start;
      padding: 5px 8px;
      grid-column: 1;
    }

    .gift-line { grid-column:1; }

    .selected-mark {
      align-items:center;
      background:var(--accent);
      border:1px solid var(--accent);
      color:#fff;
      display:inline-flex;
      font-size:.85rem;
      font-weight:950;
      gap:6px;
      grid-column:3;
      grid-row:1 / span 2;
      justify-self:end;
      padding:8px 11px;
      white-space:nowrap;
    }

    .package-card.selected .badge { display:none; }

    .package-card small,
    .checkout-card p {
      color: var(--muted);
      line-height: 1.55;
    }

    .checkout-card {
      align-self: start;
      display: grid;
      gap: 18px;
      padding: 28px;
      position: sticky;
      top: 120px;
    }

    .checkout-card.has-selection {
      border:2px solid var(--accent);
      box-shadow:0 0 0 3px #eadcf5, 0 3px 8px color-mix(in srgb, var(--shadow) 38%, transparent);
    }

    .checkout-card h2 {
      color: var(--ink);
      margin: 0;
    }

    .summary {
      align-items: center;
      background: color-mix(in srgb, var(--paper) 54%, transparent);
      border-radius: 22px;
      display: flex;
      justify-content: space-between;
      padding: 16px;
    }

    .checkout-card button {
      background: var(--ink);
      border: 0;
      border-radius: 999px;
      color: var(--paper);
      cursor: pointer;
      font: inherit;
      font-weight: 950;
      padding: 14px 18px;
    }

    .checkout-card .purchase-button:not(:disabled) {
      background:var(--accent);
      border:2px solid color-mix(in srgb, var(--accent) 70%, #000);
      border-radius:4px;
      font-size:1rem;
    }

    .checkout-card button:disabled {
      cursor: not-allowed;
      opacity: .45;
    }

    pre {
      background: color-mix(in srgb, var(--paper) 72%, transparent);
      border-radius: 18px;
      color: var(--muted);
      overflow: auto;
      padding: 14px;
      white-space: pre-wrap;
    }

    .error {
      background: color-mix(in srgb, #ff4d4f 13%, transparent);
      border: 1px solid color-mix(in srgb, #ff4d4f 28%, transparent);
      border-radius: 18px;
      color: var(--ink);
      padding: 12px 14px;
    }

    @media (max-width: 900px) {
      .credit-layout,
      .packages {
        grid-template-columns: 1fr;
      }

      .checkout-card {
        position: static;
      }

      .credit-balance {
        padding: 10px 14px;
      }

      .credit-balance > div { justify-content:space-between; width:100%; }
      .campaign-option { align-items:start; }
    }

    @media (max-width: 680px) {
      .credit-layout { padding:20px 14px 60px; }
      .package-card { grid-template-columns:minmax(0,1fr) auto; }
      .package-card .amount { grid-column:2; }
      .badge,
      .selected-mark { grid-column:1 / -1; grid-row:auto; justify-self:start; }
    }

    @media (prefers-reduced-motion: reduce) {
      .credit-balance.celebrate strong,
      .celebrate .spark,
      .package-card.popular-card::after {
        animation: none;
      }
    }
  `]
})
export class CreditTopUpPage {
  private readonly api = inject(ApiClientService);
  private readonly pageData = inject(PageDataService);
  private readonly site = inject(SiteConfigService);
  private readonly auth = inject(AuthService);
  private readonly localePreference = inject(LocalePreferenceService);
  private readonly seo = inject(SeoService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  protected readonly i18n = inject(I18nService);
  protected readonly selected = signal<WalletTopUpPackage | null>(null);
  protected readonly payload = signal('');
  protected readonly loading = signal(false);
  protected readonly error = signal('');
  protected readonly notice = signal('');
  protected readonly noticeSuccess = signal(false);
  protected readonly balance = signal<number | null>(null);
  protected readonly balanceAnimating = signal(false);
  protected readonly packages = signal<WalletTopUpPackage[]>([]);
  protected readonly campaigns = signal<WalletCampaign[]>([]);
  protected readonly appliedCampaign = signal<WalletCampaign | null>(null);
  private readonly gatewayCreditMode = signal<boolean | null>(null);
  protected readonly copy = computed<CreditCopy>(() => {
    const lang = this.i18n.lang();
    const copies: Record<string, CreditCopy> = {
      tr: { eyebrow:'Kredi al', title:'Fal deneyimini kesintisiz sürdür.', lead:'Canlı yayın hediyeleri, özel sorular ve fal siparişleri için kredi paketini seç.', campaignsTitle:'Kredi kampanyaları', credit:'kredi', popular:'Popüler', checkoutEyebrow:'Kredi paketin', choose:'Paket seç', checkoutLead:'Kredi alma akışı güvenli ödeme adımında tamamlanır.', total:'Tutar', loading:'Hazırlanıyor...', pay:'Kredi yükle', firstPurchase:'İlk alışveriş kampanyası', threshold:'Kredi kampanyası', bonus:'Ek hediye', from:'itibaren', apply:'Uygula', applied:'Uygulandı', package:'Kredi Paketi', packageDescription:'Fal Deryası kredi paketi', verifying:'Ödeme doğrulanıyor, kredilerin cüzdanına ekleniyor...', startFailed:'Kredi alma akışı başlatılamadı. Lütfen tekrar deneyin.', loadFailed:'Kredi paketleri alınamadı. Lütfen tekrar dene.', lastUse:'Son kullanım', creditedResuming:'Kredilerin yüklendi. Fal siparişin tamamlanıyor...', creditedReady:'Kredin başarıyla yüklendi. Yeni bakiyen hazır.', paymentFailed:'Ödeme tamamlanamadı. Kartından başarılı bir tahsilat yapılmadı.', bankPending:'Ödeme sonucu işleniyor. Banka onayı bekleniyor; bu ekran otomatik güncellenecek.', verificationPending:'Ödeme doğrulaması sürüyor. Bağlantı kurulduğunda bakiye otomatik güncellenecek.', notificationPending:'Ödeme bildirimi henüz ulaşmadı. Sayfayı yenilediğinde doğrulama otomatik devam edecek.' },
      en: { eyebrow:'Buy credits', title:'Keep your reading experience flowing.', lead:'Choose a credit package for live gifts, private questions and readings.', campaignsTitle:'Credit campaigns', credit:'credits', popular:'Popular', checkoutEyebrow:'Your credit package', choose:'Choose a package', checkoutLead:'Complete your credit purchase in the secure payment step.', total:'Total', loading:'Preparing...', pay:'Buy credits and continue', firstPurchase:'First purchase campaign', threshold:'Credit campaign', bonus:'bonus', from:'from', apply:'Apply', applied:'Applied', package:'Credit package', packageDescription:'Fal Deryası credit package', verifying:'Verifying payment and adding credits to your wallet...', startFailed:'The credit purchase could not be started. Please try again.', loadFailed:'Credit packages could not be loaded. Please try again.', lastUse:'Valid until', creditedResuming:'Your credits were added. Your reading order is being completed...', creditedReady:'Your credits were added successfully. Your new balance is ready.', paymentFailed:'Payment could not be completed. Your card was not charged successfully.', bankPending:'The payment result is being processed. Waiting for bank approval; this screen will update automatically.', verificationPending:'Payment verification continues. Your balance will update when the connection is restored.', notificationPending:'The payment notification has not arrived yet. Verification will continue automatically when you refresh.' },
      de: { eyebrow:'Guthaben kaufen', title:'Dein Wahrsage-Erlebnis ohne Unterbrechung.', lead:'Wähle Guthaben für Live-Geschenke, private Fragen und Deutungen.', campaignsTitle:'Guthabenaktionen', credit:'Credits', popular:'Beliebt', checkoutEyebrow:'Dein Guthabenpaket', choose:'Paket wählen', checkoutLead:'Der Kauf wird im sicheren Zahlungsschritt abgeschlossen.', total:'Summe', loading:'Wird vorbereitet...', pay:'Kaufen und weiter', firstPurchase:'Aktion für den ersten Einkauf', threshold:'Guthabenaktion', bonus:'Bonus', from:'ab', apply:'Anwenden', applied:'Angewendet', package:'Credit-Paket', packageDescription:'Fal Deryası Credit-Paket', verifying:'Zahlung wird geprüft und Credits werden gutgeschrieben...', startFailed:'Der Credit-Kauf konnte nicht gestartet werden. Bitte versuche es erneut.', loadFailed:'Credit-Pakete konnten nicht geladen werden. Bitte versuche es erneut.', lastUse:'Gültig bis', creditedResuming:'Deine Credits wurden gutgeschrieben. Deine Bestellung wird abgeschlossen...', creditedReady:'Deine Credits wurden erfolgreich gutgeschrieben. Dein neuer Stand ist verfügbar.', paymentFailed:'Die Zahlung konnte nicht abgeschlossen werden. Deine Karte wurde nicht erfolgreich belastet.', bankPending:'Die Zahlung wird verarbeitet. Die Bankbestätigung steht aus; diese Seite wird automatisch aktualisiert.', verificationPending:'Die Zahlungsprüfung läuft weiter. Dein Guthaben wird aktualisiert, sobald die Verbindung wiederhergestellt ist.', notificationPending:'Die Zahlungsbestätigung ist noch nicht eingetroffen. Nach dem Aktualisieren wird die Prüfung automatisch fortgesetzt.' },
      es: { eyebrow:'Comprar créditos', title:'Continúa tu experiencia de lectura.', lead:'Elige créditos para regalos en directo, preguntas privadas y lecturas.', campaignsTitle:'Campañas de créditos', credit:'créditos', popular:'Popular', checkoutEyebrow:'Tu paquete', choose:'Elige un paquete', checkoutLead:'Completa la compra en el paso de pago seguro.', total:'Total', loading:'Preparando...', pay:'Comprar créditos y continuar', firstPurchase:'Campaña de primera compra', threshold:'Campaña de créditos', bonus:'bonificación', from:'desde', apply:'Aplicar', applied:'Aplicada', package:'Paquete de créditos', packageDescription:'Paquete de créditos de Fal Deryası', verifying:'Verificando el pago y añadiendo los créditos...', startFailed:'No se pudo iniciar la compra de créditos. Inténtalo de nuevo.', loadFailed:'No se pudieron cargar los paquetes. Inténtalo de nuevo.', lastUse:'Válido hasta', creditedResuming:'Tus créditos se añadieron. Estamos completando tu pedido...', creditedReady:'Tus créditos se añadieron correctamente. Tu nuevo saldo está listo.', paymentFailed:'No se pudo completar el pago. No se realizó un cargo correcto en tu tarjeta.', bankPending:'Se está procesando el resultado. Esperamos la aprobación del banco; esta pantalla se actualizará automáticamente.', verificationPending:'La verificación continúa. Tu saldo se actualizará cuando se restablezca la conexión.', notificationPending:'Aún no llegó la notificación del pago. La verificación continuará al actualizar la página.' },
      fr: { eyebrow:'Acheter des crédits', title:'Poursuis ton expérience de voyance.', lead:'Choisis des crédits pour les cadeaux en direct, questions privées et voyances.', campaignsTitle:'Offres crédits', credit:'crédits', popular:'Populaire', checkoutEyebrow:'Ton forfait', choose:'Choisir un forfait', checkoutLead:'Finalise ton achat à l’étape de paiement sécurisé.', total:'Total', loading:'Préparation...', pay:'Acheter et continuer', firstPurchase:'Offre premier achat', threshold:'Offre crédits', bonus:'bonus', from:'à partir de', apply:'Appliquer', applied:'Appliquée', package:'Forfait de crédits', packageDescription:'Forfait de crédits Fal Deryası', verifying:'Vérification du paiement et ajout des crédits...', startFailed:'Impossible de démarrer l’achat de crédits. Réessayez.', loadFailed:'Impossible de charger les forfaits. Réessayez.', lastUse:'Valable jusqu’au', creditedResuming:'Vos crédits ont été ajoutés. Votre commande est en cours de finalisation...', creditedReady:'Vos crédits ont été ajoutés. Votre nouveau solde est disponible.', paymentFailed:'Le paiement n’a pas abouti. Votre carte n’a pas été débitée avec succès.', bankPending:'Le paiement est en cours de traitement. En attente de la banque ; cet écran se mettra à jour automatiquement.', verificationPending:'La vérification continue. Votre solde sera actualisé dès le retour de la connexion.', notificationPending:'La notification de paiement n’est pas encore arrivée. La vérification reprendra automatiquement après actualisation.' }
    };
    return copies[lang] ?? copies['tr'];
  });
  protected readonly creditMode = computed(() => this.gatewayCreditMode() ?? (this.site.config().pricingMode === 'credit' || this.site.config().paymentMode === 'credit'));
  protected balanceLabel(): string {
    return ({tr:'Kredi bakiyesi',en:'Credit balance',de:'Guthabenstand',es:'Saldo de créditos',fr:'Solde de crédits'} as Record<string,string>)[this.i18n.lang()] ?? 'Credit balance';
  }
  protected creditedLabel(): string {
    return ({tr:'Krediler hesabına yüklendi.',en:'Credits were added to your account.',de:'Das Guthaben wurde deinem Konto gutgeschrieben.',es:'Los créditos se añadieron a tu cuenta.',fr:'Les crédits ont été ajoutés à ton compte.'} as Record<string,string>)[this.i18n.lang()] ?? 'Credits were added to your account.';
  }
  protected emptyLabel(): string {
    return ({tr:'Şu anda satın alınabilir kredi paketi bulunmuyor.',en:'No credit packages are currently available.',de:'Derzeit sind keine Guthabenpakete verfügbar.',es:'No hay paquetes de créditos disponibles.',fr:'Aucun forfait de crédits disponible.'} as Record<string,string>)[this.i18n.lang()] ?? 'No credit packages are currently available.';
  }
  protected selectedLabel(): string {
    return ({tr:'Seçildi',en:'Selected',de:'Ausgewählt',es:'Seleccionado',fr:'Sélectionné'} as Record<string,string>)[this.i18n.lang()] ?? 'Selected';
  }
  protected currencyLabel(currency: string): string {
    return String(currency || 'TRY').toUpperCase() === 'TRY' ? 'TL' : String(currency || '').toUpperCase();
  }
  protected numberText(value: number | null | undefined, maximumFractionDigits = 2): string {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount)) return '0';
    try {
      return new Intl.NumberFormat(this.i18n.locale(), {
        minimumFractionDigits: 0,
        maximumFractionDigits
      }).format(amount);
    } catch {
      return String(amount);
    }
  }
  protected selectedPurchaseLabel(item: WalletTopUpPackage): string {
    const suffix = ({tr:'satın al',en:'buy now',de:'jetzt kaufen',es:'comprar ahora',fr:'acheter'} as Record<string,string>)[this.i18n.lang()] ?? 'buy now';
    return `${this.packageName(item)} · ${suffix}`;
  }
  protected packageName(item:WalletTopUpPackage):string{return `${this.numberText(item.creditAmount??item.amount,0)} ${this.copy().credit}`;}
  protected packageDescription(_item:WalletTopUpPackage):string{return this.copy().packageDescription;}
  private readonly resumeCheckoutID = Number(this.route.snapshot.queryParamMap.get('resumeCheckoutID') || 0);
  private readonly fortuneJourneyToken = this.fortuneDraftValue('journeyToken');
  private readonly fortuneClaimToken = this.fortuneDraftValue('claimToken');
  private readonly fortuneReturnPath = this.safeFortuneReturn(String(this.route.snapshot.queryParamMap.get('fortuneReturn') || ''));
  private fortunePaymentVerified = false;

  constructor() {
    this.seo.setPage({
      title: this.i18n.t('buyCredits'),
      description: this.i18n.t('creditSeoDescription'),
      path: this.i18n.routePath('creditTopUp')
    });
    void this.loadBalance();
    void this.initializePackages();
    if (this.fortuneJourneyToken) void this.trackFortuneJourney('credit_screen_opened', 'credit');
    const payment = this.route.snapshot.queryParamMap.get('payment');
    const checkoutID = Number(this.route.snapshot.queryParamMap.get('checkoutID') || 0);
    if (payment === 'success' && checkoutID > 0) {
      this.notice.set(this.copy().verifying);
      void this.verifyTopUp(checkoutID);
    }
  }

  private async loadBalance(): Promise<void> {
    if (typeof window === 'undefined') return;
    await this.auth.refreshCreditBalance();
    const current = this.auth.creditBalance();
    const amount = Number(current);
    if (current !== null && Number.isFinite(amount)) this.balance.set(amount);
  }

  protected preparePayload(): void {
    const item = this.selected();
    if (!item) {
      return;
    }
    this.payload.set(JSON.stringify({
      type: this.creditMode() ? 'creditTopUp' : 'walletTopUp',
      creditPackageID: item.creditPackageID,
      productID: item.productID,
      walletTopUpPresetID: this.creditMode() ? null : item.walletTopUpPresetID,
      amount: item.amount,
      currency: item.currency
    }, null, 2));
  }

  protected async startTopUp(): Promise<void> {
    const item = this.selected();
    if (!this.auth.loggedIn()) {
      this.auth.requestLogin();
      return;
    }
    if (!item || this.loading()) {
      return;
    }
    this.loading.set(true);
    this.error.set('');
    try {
      const response = await firstValueFrom(this.api.post<Record<string, unknown>>(
        (this.creditMode() ? 'creditTopUpStart' : 'walletTopUpStart'),
        {
        ...(this.creditMode() ? { creditPackageID: item.creditPackageID } : { walletTopUpPresetID: item.walletTopUpPresetID }),
        campaignID: this.appliedCampaign()?.id || null,
        resumeCheckoutID: this.resumeCheckoutID || null
        }
      ));
      const data = response['data'] as Record<string, unknown> | undefined;
      const checkoutID = Number(data?.['checkoutID'] ?? data?.['walletCheckoutID'] ?? 0);
      if (checkoutID > 0) {
        await this.trackFortuneJourney('credit_payment_started', 'payment_started', checkoutID);
        await this.router.navigateByUrl(`${this.i18n.routePath('creditTopUpCheckout', { id: checkoutID })}${this.fortuneQuery()}`);
        return;
      }
      this.payload.set(JSON.stringify(response, null, 2));
    } catch {
      this.error.set(this.copy().startFailed);
    } finally {
      this.loading.set(false);
    }
  }

  private async initializePackages(): Promise<void> {
    await this.localePreference.load();
    // SSR data can contain a stale wallet-mode snapshot. The credit page is
    // store/gateway driven, so refresh its package list in the browser from
    // the current tenant API before rendering the selectable packages.
    if (typeof window !== 'undefined') {
      await this.loadPackages();
      return;
    }
    this.loadSeededPackages();
  }

  private async loadPackages(): Promise<void> {
    await this.site.ready();
    try {
      const response = await firstValueFrom(this.api.post<unknown>('subscriptionListings', {}));
      this.setCampaigns(response);
      const items = this.extractListings(response)
        .filter((item) => this.creditMode() ? item['type'] === 'creditTopUp' : item['type'] === 'walletTopUp')
        .map((item, index) => this.normalizePackage(item, index));
      if (items.length) {
        this.packages.set(items);
      } else {
        this.error.set(this.emptyLabel());
      }
    } catch {
      this.packages.set([]);
      this.error.set(this.copy().loadFailed);
    }
    this.selected.set(null);
  }

  private loadSeededPackages(): boolean {
    const seeded = this.pageData.get<unknown>('subscriptionListings');
    this.setCampaigns(seeded);
    const items = this.extractListings(seeded)
      .filter((item) => this.creditMode() ? item['type'] === 'creditTopUp' : item['type'] === 'walletTopUp')
      .map((item, index) => this.normalizePackage(item, index));
    if (!items.length) {
      return false;
    }
    this.packages.set(items);
    this.selected.set(null);
    return true;
  }

  private extractListings(response: unknown): Record<string, unknown>[] {
    const root = response as Record<string, unknown>;
    const data = root?.['data'] as Record<string, unknown> | undefined;
    return Array.isArray(data?.['listings']) ? data['listings'] as Record<string, unknown>[] : [];
  }

  private setCampaigns(response: unknown): void {
    const root = response as Record<string, unknown>;
    const data = root?.['data'] as Record<string, unknown> | undefined;
    if (typeof data?.['creditMode'] === 'boolean') this.gatewayCreditMode.set(data['creditMode']);
    const rows = Array.isArray(data?.['campaigns']) ? data['campaigns'] : [];
    this.campaigns.set(rows.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object').map((row) => ({
      id: Number(row['id'] ?? 0), title: String(row['title'] ?? ''), userRole: row['userRole'] ? String(row['userRole']) : null,
      campaignType: String(row['campaignType'] ?? ''), discountType: row['discountType'] ? String(row['discountType']) : null,
      discountValue: row['discountValue'] === null || row['discountValue'] === undefined ? null : Number(row['discountValue']),
      minAmount: row['minAmount'] === null || row['minAmount'] === undefined ? null : Number(row['minAmount']),
      maxAmount: row['maxAmount'] === null || row['maxAmount'] === undefined ? null : Number(row['maxAmount']),
      listingIDs: Array.isArray(row['listingIDs']) ? row['listingIDs'].map((id) => Number(id)) : [],
      usageLimit: row['usageLimit'] === null || row['usageLimit'] === undefined ? null : Number(row['usageLimit']),
      userUsageLimit: row['userUsageLimit'] === null || row['userUsageLimit'] === undefined ? null : Number(row['userUsageLimit']),
      endDate: row['endDate'] ? String(row['endDate']) : null
    })).filter((row) => row.id > 0 && row.title));
    const applied = this.appliedCampaign();
    if (applied && !this.campaigns().some((campaign) => campaign.id === applied.id)) {
      this.appliedCampaign.set(null);
    }
  }

  protected campaignCanApply(campaign: WalletCampaign, item: WalletTopUpPackage | null): boolean {
    if (!item) return false;
    const listingID=this.creditMode() ? item.creditPackageID : item.walletTopUpPresetID;
    if (campaign.listingIDs.length && !campaign.listingIDs.includes(listingID)) return false;
    return campaign.minAmount === null || this.campaignBase(item) >= campaign.minAmount;
  }

  protected applyCampaign(campaign: WalletCampaign): void {
    const current = this.selected();
    if (current && this.campaignCanApply(campaign, current)) {
      this.appliedCampaign.set(campaign);
      return;
    }
    const matching = this.packages().find((item) => this.campaignCanApply(campaign, item));
    if (matching) {
      this.selected.set(matching);
      this.appliedCampaign.set(campaign);
    }
  }

  protected selectPackage(item: WalletTopUpPackage): void {
    this.selected.set(item);
    const campaign = this.appliedCampaign();
    if (campaign && !this.campaignCanApply(campaign, item)) this.appliedCampaign.set(null);
  }

  protected campaignBonus(item: WalletTopUpPackage): number {
    const campaign = this.appliedCampaign();
    if (!campaign || !this.campaignCanApply(campaign, item) || campaign.discountValue === null) return 0;
    return campaign.discountType === 'amount' ? campaign.discountValue : this.campaignBase(item) * campaign.discountValue / 100;
  }

  protected campaignBase(item: WalletTopUpPackage): number {
    return this.creditMode() ? Number(item.creditAmount ?? item.amount) : Number(item.amount);
  }

  protected campaignEnd(campaign: WalletCampaign): string {
    if (!campaign.endDate) return '';
    return `${this.copy().lastUse}: ${new Date(campaign.endDate).toLocaleDateString(this.i18n.locale())}`;
  }

  protected campaignLabel(campaign: WalletCampaign): string {
    return campaign.userRole === 'newCustomer' ? this.copy().firstPurchase : this.copy().threshold;
  }

  protected campaignDescription(campaign: WalletCampaign): string {
    if (campaign.minAmount !== null && campaign.discountValue !== null) {
      return `${this.copy().from} ${campaign.minAmount.toLocaleString()} ${this.copy().credit}: %${campaign.discountValue} ${this.copy().bonus}.`;
    }
    return `${campaign.discountValue ?? 0}% ${this.copy().bonus}.`;
  }

  private normalizePackage(item: Record<string, unknown>, index: number): WalletTopUpPackage {
    const amount=Number(item['amount'] ?? item['price'] ?? item['priceAmount'] ?? 0);
    return {
      id: Number(item['id'] ?? index + 1),
      creditPackageID: Number(item['creditPackageID'] ?? (this.creditMode() ? item['id'] : 0)),
      name: String(item['name'] ?? this.copy().package),
      description: String(item['description'] ?? this.copy().packageDescription),
      amount,
      currency: String(item['currency'] ?? item['priceCurrency'] ?? 'TRY'),
      productID: String(item['productID'] ?? ''),
      walletTopUpPresetID: Number(item['walletTopUpPresetID'] ?? (this.creditMode() ? 0 : item['id']) ?? 0),
      highlighted: Boolean(item['isHighlighted'])
      ,popular: Boolean(item['isPopular'] ?? item['popular']),
      creditAmount: Number(item['creditAmount'] ?? (this.creditMode() ? Math.ceil(amount) : 0)) || undefined
    };
  }

  private async verifyTopUp(checkoutID: number): Promise<void> {
    for (let attempt = 0; attempt < 60; attempt += 1) {
      try {
        const response = await firstValueFrom(this.api.post<Record<string, unknown>>('walletTopUpStatus', { checkoutID }));
        const data = (response['data'] ?? {}) as Record<string, unknown>;
        const currentBalance = Number(data['balance']);
        if (Number.isFinite(currentBalance) && this.balance() === null) {
          this.balance.set(currentBalance);
        }
        if (data['completed']) {
          if (!this.fortunePaymentVerified) {
            await this.trackFortuneJourney('credit_payment_verified', 'payment_verified', checkoutID);
            this.fortunePaymentVerified = true;
          }
          const creditedAmount = Math.max(0, Number(data['creditedAmount'] ?? 0));
          if (Number.isFinite(currentBalance)) {
            const previousBalance = this.balance();
            const startBalance = previousBalance === null || previousBalance === currentBalance
              ? Math.max(0, currentBalance - creditedAmount)
              : previousBalance;
            this.animateBalance(startBalance, currentBalance);
          }
          const resumeCheckoutID = Number(data['resumeCheckoutID'] ?? 0);
          if (resumeCheckoutID > 0) {
            this.notice.set(this.copy().creditedResuming);
            await this.completeResumedCheckout(resumeCheckoutID);
            return;
          }
          this.noticeSuccess.set(true);
          this.notice.set(this.copy().creditedReady);
          if (this.fortuneReturnPath) await this.router.navigateByUrl(this.fortuneReturnPath);
          return;
        }
        if (data['failed']) {
          this.notice.set(this.copy().paymentFailed);
          return;
        }
        if (attempt === 4) {
          this.notice.set(this.copy().bankPending);
        }
      } catch {
        if (attempt === 4) {
          this.notice.set(this.copy().verificationPending);
        }
      }
      await new Promise((resolve) => setTimeout(resolve, attempt < 15 ? 1000 : 2000));
    }
    this.notice.set(this.copy().notificationPending);
  }

  private animateBalance(from: number, to: number): void {
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
      this.balance.set(to);
      return;
    }
    const startedAt = Date.now();
    const duration = 1150;
    this.balanceAnimating.set(true);
    const frame = () => {
      const progress = Math.min(1, (Date.now() - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.balance.set(from + ((to - from) * eased));
      if (progress < 1) {
        requestAnimationFrame(frame);
        return;
      }
      this.balance.set(to);
      window.setTimeout(() => this.balanceAnimating.set(false), 250);
    };
    requestAnimationFrame(frame);
  }

  private async completeResumedCheckout(checkoutID: number): Promise<void> {
    const response = await firstValueFrom(this.api.post<Record<string, unknown>>('checkoutComplete', {
      checkoutID,
      paymentMethod: 'wallet',
      distanceSalesContract: true,
      preliminaryInformationForm: true,
      sameShippingAddress: true,
      billingAddressSame: true,
      billingAddresSame: true
    }));
    const orderID = Number(this.find(response, 'orderID') ?? this.find(response, 'id') ?? 0);
    if (orderID <= 0) {
      throw new Error('resumed_checkout_failed');
    }
    this.noticeSuccess.set(true);
    await this.router.navigateByUrl(this.i18n.routePath('orderDetail', { slug: orderID }));
  }

  private find(value: unknown, key: string): unknown {
    if (!value || typeof value !== 'object') return undefined;
    if (!Array.isArray(value) && key in (value as Record<string, unknown>)) return (value as Record<string, unknown>)[key];
    for (const child of Object.values(value as Record<string, unknown>)) {
      const found = this.find(child, key);
      if (found !== undefined) return found;
    }
    return undefined;
  }

  private safeFortuneReturn(value: string): string {
    const path=String(value||'').trim();
    return path.startsWith('/')&&!path.startsWith('//')&&!/[\\\r\n]/.test(path)?path:'';
  }

  private fortuneQuery(): string {
    if(!this.fortuneJourneyToken)return '';
    const query=new URLSearchParams();if(this.fortuneReturnPath)query.set('fortuneReturn',this.fortuneReturnPath);
    return `?${query.toString()}`;
  }

  private fortuneDraftValue(key:string):string{if(typeof window==='undefined')return '';try{const draft=JSON.parse(window.localStorage.getItem('fortune.category-order-draft.v2')||'null');return draft&&typeof draft==='object'?String(draft[key]||''):''}catch{return ''}}

  private async trackFortuneJourney(eventType:string,stage:string,checkoutID=0):Promise<void>{
    if(!this.fortuneJourneyToken)return;
    try{await firstValueFrom(this.api.post<Record<string,unknown>>('fortuneCategoryJourney',{journeyToken:this.fortuneJourneyToken,claimToken:this.fortuneClaimToken,eventType,stage,...(checkoutID>0?{checkoutID}:{})}));}catch{}
  }
}
